import React, { useState } from 'react';
import { 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack
} from '@mui/material';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { setUserDetails } from '../../store.js';
import useAlert from '../../alert/useAlert';
import { supabase } from '../../supabaseClient';

const PrivacySettings = () => {
  const { userDetails } = useSelector((state) => ({userDetails: state.globalState.userDetails}), shallowEqual);
  const currentOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);
  const dispatch = useDispatch(); 
  const { setAlert } = useAlert();
  
  const [dataRetentionDays, setDataRetentionDays] = useState(userDetails?.data_retention_days || null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  const handleSaveRetentionDays = async () => {
    try {
      setIsLoading(true);
      
      const { error } = await supabase
        .from('user_accounts')
        .update({ data_retention_days: dataRetentionDays ? dataRetentionDays : null })
        .eq('id', userDetails.id);
      
      if (error) throw error;
      dispatch(setUserDetails({ ...userDetails, data_retention_days: dataRetentionDays }));
      setAlert('Data retention period updated successfully', 'success');
    } catch (error) {
      console.error('Error updating retention period:', error);
      setAlert('Failed to update data retention period', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRemoveAllData = async () => {
    try {
      setIsLoading(true);
      setDeleteDialogOpen(false);
  
      const tablesByIdField = {
        teamid: ['patients'],
      };
  
      const allDeletions = [];
  
      for (const [idField, tables] of Object.entries(tablesByIdField)) {
        for (const table of tables) {
          const deletePromise = (async () => {
            const { error } = await supabase
              .from(table)
              .delete()
              .eq(idField, idField === 'teamid' ? currentOrg.id : userDetails.id);
            if (error) {
              const tableError = new Error(`Error deleting from table ${table}`);
              tableError.table = table;
              tableError.originalError = error;
              throw tableError;
            }
            return { table, success: true };
          })();
  
          allDeletions.push(deletePromise);
        }
      }  
      await Promise.all(allDeletions);
      setAlert('All your data has been permanently removed', 'success');
    } catch (error) {
      console.error('Error removing data:', error);
      if (error.table) {
        setAlert(`Failed to delete data from ${error.table}`, 'error');
      } else {
        setAlert('Failed to remove all data', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };
  

  return (
    <div>
      <Typography variant="h5" sx={{ marginBottom: 3 }}>
        Privacy Settings
      </Typography>
      <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '10px', padding: '24px', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)' }}>
        {/* Row 1: Text field for data retention */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Enter number of days for patient data retention (days)</Typography>
          <TextField
            fullWidth
            type="number"
            value={dataRetentionDays}
            onChange={(e) => setDataRetentionDays(e.target.value)}
            InputProps={{ inputProps: { min: 1 } }}
          />
        </Box>
        
        {/* Row 2: Save button */}
        <Box sx={{ mb: 3 }}>
          <Button 
            variant="outlined" 
            onClick={handleSaveRetentionDays}
            disabled={isLoading}
            sx={{width: '100%', height: '50px', borderRadius: 30, backgroundColor: '#29C0BB','&:hover': { backgroundColor: '#1BA8A3' }, border: 0}}
          >
            <Typography variant="button" sx={{color: '#FFFFFF', fontSize: '15px'}}>Save</Typography>
          </Button>
        </Box>
        
        {/* Row 3: Remove all data button */}
        <Box>
          <Button 
            variant="contained" 
            onClick={() => setDeleteDialogOpen(true)}
            disabled={isLoading}
            sx={{width: '100%', height: '50px', borderRadius: 30, backgroundColor: '#FF4842','&:hover': { backgroundColor: '#FF3326' }, border: 0}}
          >
            <Typography variant="button" sx={{color: '#FFFFFF', fontSize: '15px'}}>Remove All Data</Typography>
          </Button>
        </Box>
      </Box>
      
      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ color: '#FF4842' }}>⚠️ Delete All Your Data</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>Are you sure you want to delete all your data?</Typography>
            <Typography sx={{ 
              backgroundColor: '#FFF4F2', 
              color: '#FF4842',
              p: 2,
              borderRadius: 1,
              border: '1px solid #FFE9E7'
            }}>
              <strong>Warning:</strong> This action is irreversible. Deleting will permanently remove all the patient data, including:
              <ul style={{ marginTop: '8px', marginBottom: '4px' }}>
                <li>All patient data and history</li>
                <li>All medical records and notes</li>
                <li>All generated medical reports</li>
                <li>All summary configurations</li>
              </ul>
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            sx={{color: '#29C0BB'}} 
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            sx={{
              backgroundColor: '#FF4842',
              color: 'white',
              '&:hover': {
                backgroundColor: '#FF3326'
              }
            }} 
            variant="contained"
            onClick={handleRemoveAllData}
            disabled={isLoading}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PrivacySettings;